
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, ArrowRight } from "lucide-react"
import { Button } from "@components/react/pricing/ui/button"
import { FedRampBadge } from "@components/react/pricing/fedramp-badge"
import { pricingData, getAllFeaturesForCategory } from "@components/react/pricing/lib/pricing-data"
import { FeatureCheck } from "@components/react/pricing/plan-comparison/feature-check"
import { PricingCell } from "@components/react/pricing/plan-comparison/pricing-cell"
import { PlanSelectButton } from "@components/react/pricing/plan-comparison/plan-select-button"

interface ResponsivePlanComparisonProps {
  openQuoteModalWithPlan: (planName: string, planType?: string) => void
  openQuoteModalForVolumePricing: (planName: string) => void
}

export function ResponsivePlanComparison({
  openQuoteModalWithPlan,
  openQuoteModalForVolumePricing,
}: ResponsivePlanComparisonProps) {
  const [activeTab, setActiveTab] = useState<"control" | "resilience">("control")
  const [isMobile, setIsMobile] = useState(false)
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null)

  // Mobile-specific state
  const [activeCategory, setActiveCategory] = useState<string | null>("control")
  const [activePlan, setActivePlan] = useState<string | null>(null)

  // Check if we're on mobile with debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const checkIfMobile = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768)
      }, 100)
    }

    // Initial check
    checkIfMobile()

    // Add event listener
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile)
      clearTimeout(timeoutId)
    }
  }, [])

  // Helper functions for mobile view
  const toggleCategory = (categoryId: string) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId)
    setActivePlan(null) // Reset active plan when changing categories
  }

  const togglePlan = (planId: string) => {
    setActivePlan(activePlan === planId ? null : planId)
  }

  // Get current category data
  const categoryData = pricingData[activeTab]
  const features = getAllFeaturesForCategory(activeTab)

  // Helper function to adjust a hex color (lighten/darken)
  const adjustColor = (hex: string, percent: number) => {
    // Remove the # if present
    hex = hex.replace("#", "")

    // Parse the hex values
    let r = Number.parseInt(hex.substring(0, 2), 16)
    let g = Number.parseInt(hex.substring(2, 4), 16)
    let b = Number.parseInt(hex.substring(4, 6), 16)

    // Adjust each component (negative percent darkens, positive lightens)
    r = Math.floor(r * (1 - percent / 100))
    g = Math.floor(g * (1 - percent / 100))
    b = Math.floor(b * (1 - percent / 100))

    // Ensure values are in valid range
    r = Math.max(0, Math.min(255, r))
    g = Math.max(0, Math.min(255, g))
    b = Math.max(0, Math.min(255, b))

    // Convert back to hex
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
  }

  // Define column background colors with different shades - Apple-style subtle differences
  type PlanLevel = "essential" | "plus" | "complete";
  const columnBgColors: Record<PlanLevel, string> = {
    essential: categoryData.planColors.essential,
    plus: categoryData.planColors.plus,
    complete: categoryData.planColors.complete,
  };

  const columnHoverBgColors: Record<PlanLevel, string> = {
    essential: adjustColor(categoryData.planColors.essential, 3),
    plus: adjustColor(categoryData.planColors.plus, 3),
    complete: adjustColor(categoryData.planColors.complete, 3),
  };

  // Render desktop table view
  const renderDesktopView = () => (
    <div className="w-full max-w-7xl mx-auto">
      <div className="flex flex-col items-center max-w-6xl mx-auto">
        {/* Badge */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block px-5 py-1.5 bg-[#ef483d] bg-opacity-10 rounded-full text-[#ef483d] text-sm font-medium">
            Compare Plans
          </span>
        </motion.div>

        {/* Main heading */}
        <motion.h2
          className="text-3xl md:text-5xl font-bold tracking-tight text-center mb-8 w-full mx-auto whitespace-normal text-[#1d1d1f]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          First, Choose the Right Public Sector Plan for Your Data Security Needs
        </motion.h2>

        {/* FedRAMP badge */}
        <motion.div
          className="mt-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <FedRampBadge variant="subtle" animated={true} />
        </motion.div>
      </div>

      {/* Color-coded tabs */}
      <motion.div
        className="flex justify-center mb-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="bg-[#f5f5f7] rounded-full p-1 shadow-sm border border-[#e5e5e7] flex w-full max-w-md">
          <button
            onClick={() => setActiveTab("control")}
            className={`flex-1 py-3 px-6 rounded-full text-base font-medium transition-all duration-300 ${
              activeTab === "control" ? "bg-[#26a69a] text-white shadow-sm" : "text-[#86868b] hover:text-[#26a69a]"
            }`}
          >
            Control
          </button>
          <button
            onClick={() => setActiveTab("resilience")}
            className={`flex-1 py-3 px-6 rounded-full text-base font-medium transition-all duration-300 ${
              activeTab === "resilience" ? "bg-[#4caf50] text-white shadow-sm" : "text-[#86868b] hover:text-[#4caf50]"
            }`}
          >
            Resilience
          </button>
        </div>
      </motion.div>

      {/* Comparison table */}
      <div className="overflow-x-auto rounded-lg shadow-sm border border-[#e5e5e7]">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5 }}
            className="min-w-full"
          >
            <table className="w-full border-collapse compare-table">
              <thead className="border-b border-[#e5e5e7] sticky top-0 z-10">
                <tr>
                  <th
                    className="py-6 px-6 text-left font-semibold text-[#1d1d1f] w-[250px] border-b border-[#e5e5e7] bg-white transition-all duration-300"
                    onMouseEnter={() => setHoveredColumn("features")}
                    onMouseLeave={() => setHoveredColumn(null)}
                  >
                    <span className="text-lg">Features</span>
                  </th>
                  {categoryData.plans.map((plan, index) => {
                    const planLevel = plan.id.split("-")[1] // essential, plus, complete
                    const isColumnHovered = hoveredColumn === planLevel

                    return (
                      <th
                        key={plan.id}
                        style={{
                          backgroundColor: columnBgColors[planLevel as PlanLevel],
                          boxShadow: isColumnHovered ? `inset 0 -2px 0 ${categoryData.color}` : "none", // Subtle underline on hover
                        }}
                        className="py-6 px-6 text-center font-semibold text-[#1d1d1f] border-b border-[#e5e5e7] transition-all duration-300"
                        onMouseEnter={() => setHoveredColumn(planLevel)}
                        onMouseLeave={() => setHoveredColumn(null)}
                      >
                        <span className="text-lg">{plan.name}</span>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Pricing row */}
                <motion.tr
                  className="transition-colors duration-300"
                  onMouseEnter={() => setHoveredRow(0)}
                  onMouseLeave={() => setHoveredRow(null)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <td
                    className={`py-5 px-6 text-[#424245] font-medium border-b border-[#e5e5e7] bg-white transition-all duration-300 ${
                      hoveredRow === 0 ? "bg-[#f8f8f8]" : ""
                    }`}
                  >
                    Pricing
                  </td>
                  {categoryData.plans.map((plan, index) => {
                    const planLevel = plan.id.split("-")[1] // essential, plus, complete
                    const isColumnHovered = hoveredColumn === planLevel
                    const isRowHovered = hoveredRow === 0

                    // Determine background color based on hover state
                    let bgColor = columnBgColors[planLevel as keyof typeof columnBgColors]
                    if (isColumnHovered || isRowHovered) {
                      bgColor = columnHoverBgColors[planLevel as keyof typeof columnHoverBgColors]; // Use hover color if hovered
                    }

                    return (
                      <td
                        key={`${plan.id}-price`}
                        style={{
                          backgroundColor: bgColor,
                          boxShadow: isColumnHovered ? `inset 0 0 0 1px ${categoryData.color}20` : "none", // Very subtle border
                        }}
                        className="py-5 px-6 text-center border-b border-[#e5e5e7] transition-all duration-300"
                      >
                        <PricingCell price={plan.price} />
                      </td>
                    )
                  })}
                </motion.tr>

                {/* Volume pricing row */}
                <motion.tr
                  className="transition-colors duration-300"
                  onMouseEnter={() => setHoveredRow(1)}
                  onMouseLeave={() => setHoveredRow(null)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                >
                  <td
                    className={`py-5 px-6 text-[#424245] font-medium border-b border-[#e5e5e7] bg-white transition-all duration-300 ${
                      hoveredRow === 1 ? "bg-[#f8f8f8]" : ""
                    }`}
                  >
                    Volume pricing
                  </td>
                  {categoryData.plans.map((plan, index) => {
                    const planLevel = plan.id.split("-")[1]
                    const isColumnHovered = hoveredColumn === planLevel
                    const isRowHovered = hoveredRow === 1

                    // Determine background color based on hover state
                    let bgColor = columnBgColors[planLevel as keyof typeof columnBgColors]
                    if (isColumnHovered || isRowHovered) {
                      bgColor = columnHoverBgColors[planLevel as keyof typeof columnHoverBgColors]; // Use hover color if hovered
                    }

                    return (
                      <td
                        key={`${plan.id}-volume`}
                        style={{
                          backgroundColor: bgColor,
                          boxShadow: isColumnHovered ? `inset 0 0 0 1px ${categoryData.color}20` : "none", // Very subtle border
                        }}
                        className="py-5 px-6 text-center border-b border-[#e5e5e7] transition-all duration-300"
                      >
                        <Button
                          variant="link"
                          className="p-0 h-auto font-medium hover:underline transition-all duration-300"
                          style={{ color: categoryData.color }}
                          onClick={() => openQuoteModalWithPlan(plan.name, "volume")}
                        >
                          Contact Us
                        </Button>
                      </td>
                    )
                  })}
                </motion.tr>

                {/* Description row */}
                <motion.tr
                  className="transition-colors duration-300"
                  onMouseEnter={() => setHoveredRow(2)}
                  onMouseLeave={() => setHoveredRow(null)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                >
                  <td
                    className={`py-5 px-6 text-[#424245] font-medium border-b border-[#e5e5e7] bg-white transition-all duration-300 ${
                      hoveredRow === 2 ? "bg-[#f8f8f8]" : ""
                    }`}
                  >
                    Description
                  </td>
                  {categoryData.plans.map((plan, index) => {
                    const planLevel = plan.id.split("-")[1]
                    const isColumnHovered = hoveredColumn === planLevel
                    const isRowHovered = hoveredRow === 2

                    // Determine background color based on hover state
                    let bgColor = columnBgColors[planLevel as keyof typeof columnBgColors]
                    if (isColumnHovered || isRowHovered) {
                      bgColor = columnHoverBgColors[planLevel as keyof typeof columnHoverBgColors]; // Use hover color if hovered
                    }

                    return (
                      <td
                        key={`${plan.id}-desc`}
                        style={{
                          backgroundColor: bgColor,
                          boxShadow: isColumnHovered ? `inset 0 0 0 1px ${categoryData.color}20` : "none", // Very subtle border
                        }}
                        className="py-5 px-6 text-center border-b border-[#e5e5e7] transition-all duration-300"
                      >
                        {plan.description}
                      </td>
                    )
                  })}
                </motion.tr>

                {/* Feature rows */}
                {features.map((feature, featureIndex) => (
                  <motion.tr
                    key={`feature-${featureIndex}`}
                    className="transition-colors duration-300"
                    onMouseEnter={() => setHoveredRow(featureIndex + 3)}
                    onMouseLeave={() => setHoveredRow(null)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 + (featureIndex + 3) * 0.05 }}
                  >
                    <td
                      className={`py-5 px-6 text-[#424245] font-medium border-b border-[#e5e5e7] bg-white transition-all duration-300 ${
                        hoveredRow === featureIndex + 3 ? "bg-[#f8f8f8]" : ""
                      }`}
                    >
                      {feature}
                    </td>
                    {categoryData.plans.map((plan) => {
                      const planLevel = plan.id.split("-")[1]
                      const featureValue = feature in plan.features ? plan.features[feature as keyof typeof plan.features] : "-";
                      const isColumnHovered = hoveredColumn === planLevel
                      const isRowHovered = hoveredRow === featureIndex + 3

                      // Determine background color based on hover state
                      let bgColor = columnBgColors[planLevel as keyof typeof columnBgColors]
                      if (isColumnHovered || isRowHovered) {
                        bgColor = columnHoverBgColors[planLevel as keyof typeof columnHoverBgColors]; // Use hover color if hovered
                      }

                      return (
                        <td
                          key={`${plan.id}-feature-${featureIndex}`}
                          style={{
                            backgroundColor: bgColor,
                            boxShadow: isColumnHovered ? `inset 0 0 0 1px ${categoryData.color}20` : "none", // Very subtle border
                          }}
                          className="py-5 px-6 text-center border-b border-[#e5e5e7] transition-all duration-300"
                        >
                          <FeatureCheck
                            value={featureValue}
                            color={categoryData.color}
                            lightColor={categoryData.lightColor}
                          />
                        </td>
                      )
                    })}
                  </motion.tr>
                ))}

                {/* Button row */}
                <tr>
                  <td className="py-6 px-6 bg-white"></td>
                  {categoryData.plans.map((plan) => {
                    const planLevel = plan.id.split("-")[1]
                    const isColumnHovered = hoveredColumn === planLevel

                    // Determine background color based on hover state
                    let bgColor = columnBgColors[planLevel as keyof typeof columnBgColors]
                    if (isColumnHovered) {
                      bgColor = columnHoverBgColors[planLevel as keyof typeof columnHoverBgColors]; // Use hover color if hovered
                    }

                    return (
                      <td
                        key={`${plan.id}-button`}
                        style={{
                          backgroundColor: bgColor,
                          boxShadow: isColumnHovered ? `inset 0 0 0 1px ${categoryData.color}20` : "none", // Very subtle border
                          transform: isColumnHovered ? "translateY(-1px)" : "translateY(0)", // Subtle lift on hover
                        }}
                        className="py-6 px-6 text-center transition-all duration-300"
                      >
                        <PlanSelectButton planName={plan.name} onClick={() => openQuoteModalWithPlan(plan.name)} />
                      </td>
                    )
                  })}
                </tr>
              </tbody>
            </table>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )

  // Render mobile accordion view
  const renderMobileView = () => (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="flex flex-col items-center mb-8">
        {/* Badge */}
        <motion.div
          className="mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block px-5 py-1.5 bg-[#ef483d] bg-opacity-10 rounded-full text-[#ef483d] text-sm font-medium">
            Compare Plans
          </span>
        </motion.div>

        {/* Main heading */}
        <motion.h2
          className="text-2xl font-bold tracking-tight text-center mb-6 w-full mx-auto text-[#1d1d1f]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          First, Choose the Right Public Sector Plan for Your Data Security Needs 
        </motion.h2>

        {/* FedRAMP badge */}
        <motion.div
          className="mt-4 mb-8 inline-flex"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <FedRampBadge variant="subtle" animated={true} />
        </motion.div>
      </div>

      {/* Plan Categories */}
      {Object.entries(pricingData).map(([categoryKey, categoryValue]) => (
        <motion.div
          key={categoryKey}
          className="rounded-2xl overflow-hidden shadow-sm bg-white border border-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <button
            onClick={() => toggleCategory(categoryKey)}
            className="w-full flex items-center justify-between p-5 text-left transition-all duration-300"
            style={{
              background:
                activeCategory === categoryKey
                  ? `linear-gradient(to right, ${categoryValue.gradientStart}, ${categoryValue.gradientEnd})`
                  : "white",
            }}
          >
            <div>
              <h3 className="text-xl font-medium" style={{ color: categoryValue.color }}>
                {categoryValue.name} Plans
              </h3>
              <p className="text-sm text-gray-500 mt-1 font-light">{categoryValue.description}</p>
            </div>
            <motion.div
              animate={{ rotate: activeCategory === categoryKey ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="h-8 w-8 rounded-full bg-white shadow-sm flex items-center justify-center"
            >
              <ChevronDown className="h-5 w-5" style={{ color: categoryValue.color }} />
            </motion.div>
          </button>

          <AnimatePresence>
            {activeCategory === categoryKey && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-5 pt-2">
                  {categoryValue.plans.map((plan) => (
                    <motion.div
                      key={plan.id}
                      className="mt-4 overflow-hidden"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <div
                        className={`rounded-xl border ${
                          activePlan === plan.id ? `border-[${categoryValue.hoverColor}] shadow-md` : "border-gray-200"
                        } overflow-hidden transition-all duration-300`}
                        style={{
                          borderColor: activePlan === plan.id ? categoryValue.hoverColor : undefined,
                        }}
                      >
                        <button
                          onClick={() => togglePlan(plan.id)}
                          className="w-full flex items-center justify-between p-4 text-left bg-white"
                        >
                          <div>
                            <h4 className="font-medium" style={{ color: categoryValue.hoverColor }}>
                              {plan.name}
                            </h4>
                            <div className="flex flex-col mt-1">
                              <span className="text-xs text-gray-500">Starting at</span>
                              <div className="flex items-baseline">
                                <span className="text-xl font-semibold">{plan.price}</span>
                                <span className="text-xs text-gray-500 ml-1">user/month</span>
                              </div>
                            </div>
                          </div>
                          <motion.div
                            animate={{ rotate: activePlan === plan.id ? 180 : 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="h-6 w-6 rounded-full bg-gray-50 flex items-center justify-center"
                          >
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          </motion.div>
                        </button>

                        <AnimatePresence>
                          {activePlan === plan.id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden border-t border-gray-100"
                            >
                              <div className="p-4 bg-gray-50 text-sm">
                                <p className="text-gray-600 mb-4">{plan.description}</p>

                                <div className="space-y-3">
                                  {Object.entries(plan.features).map(([feature, value], featureIndex) => (
                                    <div key={featureIndex} className="flex items-center justify-between">
                                      <span className="text-gray-700">{feature}</span>
                                      <div>
                                        <FeatureCheck
                                          value={value}
                                          color={categoryValue.color}
                                          lightColor={categoryValue.lightColor}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="mt-6 space-y-3">
                                  <Button
                                    className="w-full bg-[#3037e2] hover:bg-[#2731c8] text-white rounded-full py-2 h-auto group"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openQuoteModalWithPlan(plan.name)
                                    }}
                                  >
                                    <span>Get Custom Quote</span>
                                    <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                  </Button>
                                  <Button
                                    variant="link"
                                    className="w-full h-auto py-1"
                                    style={{ color: categoryValue.color }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openQuoteModalForVolumePricing(plan.name)
                                    }}
                                  >
                                    Volume pricing
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  )

  // Render the appropriate view based on screen size
  return isMobile ? renderMobileView() : renderDesktopView()
}
